// Web mirror of Android's due-date notification pipeline
// (TaskNotificationWorker + ShowTaskNotification): every not-done task that is
// due today or overdue gets ONE inbox row, deduped cross-client via the
// deterministic doc id notificationId("due-<taskId>-<dueDate>") — the same
// key Android writes, so whichever platform runs first wins and the other
// no-ops. Android fires from a daily WorkManager job; the web has no
// background scheduler, so this sweep runs once per session on app load.
// Create-if-absent: an existing row (even dismissed) is never rewritten.
//
// Android: domain/worker/TaskNotificationWorker.kt + ShowTaskNotification.kt
// iOS:     SweepDueDateNotifications (MaterializeRecurringTasks.swift)

import { collection, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import { toDocId, toRefValue } from "../../utils/ids";
import {
  notificationId,
  NOTIFICATION_TYPE,
  REMOTE_STATE_DELETE,
  REMOTE_STATE_SYNCED,
} from "../../utils/recurrence";

const TASK_STATE_DONE = "task_state_done";

export async function sweepDueDateNotifications({ db, uid }) {
  if (!db || !uid) return;
  const now = Date.now();
  // End of *local* today, matching Android's getStartAndEndOfDay(now): the
  // window is 1..endOfToday, i.e. due-today plus everything already overdue
  // (dueDate > 0 excludes the "no due date" sentinel).
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  let snapshot;
  try {
    snapshot = await getDocs(
      query(
        collection(db, `users/${uid}/tasks`),
        where("dueDate", ">", 0),
        where("dueDate", "<=", end.getTime())
      )
    );
  } catch (err) {
    console.error("[dueSweep] query failed:", err);
    return;
  }

  for (const taskDoc of snapshot.docs) {
    const task = taskDoc.data();
    if (task.usedAsNote) continue;
    if (task.taskState === TASK_STATE_DONE) continue;
    if (task.remoteState === REMOTE_STATE_DELETE) continue;

    // Same dedup key as Android's ShowTaskNotification: due-<taskId>-<dueDate>.
    const notifId = notificationId(`due-${task.id}-${task.dueDate}`);
    const notifRef = doc(db, `users/${uid}/notifications`, toDocId(notifId));
    try {
      const existing = await getDoc(notifRef);
      if (existing.exists()) continue; // fired before (here or on Android)
      await setDoc(notifRef, {
        id: notifId,
        type: NOTIFICATION_TYPE.DUE_DATE,
        title: task.title || "",
        taskId: toRefValue(task.id),
        projectId: toRefValue(task.projectId),
        firedAt: now,
        read: false,
        remoteState: REMOTE_STATE_SYNCED,
        firestoreId: toDocId(notifId),
        updatedAt: now,
      });
    } catch (err) {
      console.error("[dueSweep] task", task.id, "failed:", err);
    }
  }
}
